/**
 * DomainWizardModal — first-time onboarding for the Domains page.
 *
 * Explains what domains are, walks through the two options (free subdomain
 * vs custom), previews the DNS steps, and lands on a "Ready" screen with
 * two CTAs. Dismissable — persists a `wb:domain-wizard-seen` flag in
 * localStorage so it only auto-opens on the vendor's first visit.
 *
 * Usage:
 *   <DomainWizardModal open={open} onClose={close} onConnect={openAddModal} />
 */
import React, { useState } from 'react';
import { Globe, ArrowRight, ArrowLeft, X, Check, Shield, Zap, Server, ExternalLink, Sparkles } from 'lucide-react';

const STEPS = [
  {
    id: 'intro',
    eyebrow: 'Step 1 · Welcome',
    title: 'What is a custom domain?',
    body: (
      <>
        <p className="text-white/75 text-sm leading-relaxed">
          A custom domain is your own web address — like <code className="px-1.5 py-0.5 rounded bg-white/10 text-[#A78BFA] text-[13px]">yourshop.com</code> — instead of the free subdomain we give you at{' '}
          <code className="px-1.5 py-0.5 rounded bg-white/10 text-white/80 text-[13px]">yourshop.entreprenatives.com</code>.
        </p>
        <p className="text-white/60 text-sm mt-3 leading-relaxed">
          A custom domain builds trust, is easier to remember, and looks professional in emails, cards, and social posts. Most shoppers won&apos;t buy from a URL they don&apos;t recognise.
        </p>
        <div className="mt-5 grid grid-cols-3 gap-2">
          {[
            { icon: Shield,  label: 'More trusted',  hint: '+34% conversion' },
            { icon: Zap,     label: 'Easier to share', hint: 'Short & memorable' },
            { icon: Sparkles, label: 'Free SSL',     hint: 'Auto-provisioned' },
          ].map((b) => (
            <div key={b.label} className="p-3 rounded-lg bg-white/[0.03] border border-white/8 text-center">
              <b.icon size={16} className="text-[#A78BFA] mx-auto mb-1.5" />
              <p className="text-white text-[11px] font-bold">{b.label}</p>
              <p className="text-white/45 text-[10px] mt-0.5">{b.hint}</p>
            </div>
          ))}
        </div>
      </>
    ),
  },
  {
    id: 'options',
    eyebrow: 'Step 2 · Your options',
    title: 'Do you already own a domain?',
    body: (
      <>
        <div className="space-y-3">
          <div className="p-4 rounded-xl border border-white/10 bg-white/[0.03]" data-testid="wizard-option-owned">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-lg bg-emerald-500/15 border border-emerald-400/25 flex items-center justify-center shrink-0">
                <Check size={15} className="text-emerald-300" />
              </div>
              <div className="flex-1">
                <p className="text-white font-bold text-sm">Yes — I own one</p>
                <p className="text-white/60 text-[13px] mt-1">
                  You&apos;ll add two DNS records (A + TXT) in your registrar dashboard. Verification takes 5–30 minutes.
                </p>
              </div>
            </div>
          </div>
          <div className="p-4 rounded-xl border border-white/10 bg-white/[0.03]" data-testid="wizard-option-need-one">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-lg bg-cyan-500/15 border border-cyan-400/25 flex items-center justify-center shrink-0">
                <Globe size={15} className="text-cyan-300" />
              </div>
              <div className="flex-1">
                <p className="text-white font-bold text-sm">Not yet — I need to buy one</p>
                <p className="text-white/60 text-[13px] mt-1">
                  Domains cost ~$10–15/year. We recommend Namecheap, Cloudflare, or Google Domains. Once purchased, come back and follow the DNS steps.
                </p>
                <div className="mt-2.5 flex flex-wrap gap-1.5">
                  {[
                    { name: 'Namecheap',  url: 'https://www.namecheap.com/domains/' },
                    { name: 'Cloudflare', url: 'https://www.cloudflare.com/products/registrar/' },
                    { name: 'Porkbun',    url: 'https://porkbun.com/' },
                  ].map((r) => (
                    <a key={r.name} href={r.url} target="_blank" rel="noreferrer"
                       className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-white/5 hover:bg-white/10 border border-white/10 text-white/75 text-[10.5px] font-bold">
                      {r.name} <ExternalLink size={9} />
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div className="p-4 rounded-xl border border-white/8 bg-white/[0.015]" data-testid="wizard-option-subdomain">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center shrink-0">
                <Server size={15} className="text-white/60" />
              </div>
              <div className="flex-1">
                <p className="text-white font-bold text-sm">Skip for now</p>
                <p className="text-white/50 text-[13px] mt-1">
                  Keep using your free <code className="text-[#A78BFA]">yourshop.entreprenatives.com</code> subdomain. You can connect a custom domain any time.
                </p>
              </div>
            </div>
          </div>
        </div>
      </>
    ),
  },
  {
    id: 'dns',
    eyebrow: 'Step 3 · How DNS works',
    title: 'You\'ll add 2 records in your registrar',
    body: (
      <>
        <p className="text-white/70 text-sm leading-relaxed">
          After you add your domain here, we&apos;ll show you two DNS records to copy-paste into your domain registrar (GoDaddy, Namecheap, Cloudflare, etc.). Here&apos;s what they look like:
        </p>
        <div className="mt-4 rounded-xl border border-white/10 bg-black/30 overflow-hidden font-mono text-[12px]">
          <div className="grid grid-cols-[60px_100px_1fr] gap-2 px-3 py-2 border-b border-white/5 text-[10px] uppercase tracking-wider text-white/40 font-bold">
            <span>Type</span><span>Host</span><span>Value</span>
          </div>
          <div className="grid grid-cols-[60px_100px_1fr] gap-2 px-3 py-2.5 border-b border-white/5">
            <span className="text-violet-300 font-bold">A</span>
            <span className="text-white/70">@</span>
            <span className="text-white/85">76.76.21.21</span>
          </div>
          <div className="grid grid-cols-[60px_100px_1fr] gap-2 px-3 py-2.5">
            <span className="text-violet-300 font-bold">TXT</span>
            <span className="text-white/70">_entreprenatives</span>
            <span className="text-white/85 truncate">verify=abc123...</span>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-3 gap-2 text-[11px] text-white/60">
          {[
            { n: '1', t: 'Add domain here' },
            { n: '2', t: 'Copy records to registrar' },
            { n: '3', t: 'Click "Verify" (5-30 min)' },
          ].map((s) => (
            <div key={s.n} className="p-2 rounded-lg bg-white/[0.03] border border-white/8">
              <div className="w-5 h-5 rounded-full bg-[#A78BFA]/20 text-[#A78BFA] flex items-center justify-center text-[10px] font-bold mb-1">{s.n}</div>
              <p className="text-white/75">{s.t}</p>
            </div>
          ))}
        </div>
        <p className="mt-4 text-[11px] text-white/45">
          Free SSL certificate auto-provisions after verification. No credit card needed — we handle it.
        </p>
      </>
    ),
  },
];

export default function DomainWizardModal({ open, onClose, onConnect }) {
  const [step, setStep] = useState(0);
  if (!open) return null;
  const s = STEPS[step];
  const isLast = step === STEPS.length - 1;

  const dismiss = () => {
    try { localStorage.setItem('wb:domain-wizard-seen', '1'); } catch { /* private mode */ }
    onClose?.();
  };
  const finish = () => {
    try { localStorage.setItem('wb:domain-wizard-seen', '1'); } catch { /* private mode */ }
    onClose?.();
    onConnect?.();
  };

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={dismiss}
      data-testid="domain-wizard-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg rounded-2xl border border-white/10 bg-[#171A2C] shadow-2xl overflow-hidden"
      >
        {/* Header with progress dots */}
        <div className="relative p-6 pb-4 border-b border-white/8">
          <button
            onClick={dismiss}
            className="absolute top-4 right-4 p-1.5 rounded-lg text-white/50 hover:text-white hover:bg-white/5"
            data-testid="wizard-close"
            title="Skip walkthrough"
          >
            <X size={15} />
          </button>
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#8B5CF6] to-[#6D28D9] flex items-center justify-center shadow-[0_8px_24px_rgba(139,92,246,0.35)]">
              <Globe size={19} className="text-white" />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#A78BFA]">{s.eyebrow}</p>
              <h3 className="text-white font-bold text-lg font-syne mt-0.5">{s.title}</h3>
            </div>
          </div>
          {/* Progress dots */}
          <div className="mt-4 flex items-center gap-1.5" data-testid="wizard-progress">
            {STEPS.map((_, i) => (
              <span
                key={i}
                className={`h-1 rounded-full transition-all ${i === step ? 'w-8 bg-[#A78BFA]' : i < step ? 'w-4 bg-[#A78BFA]/50' : 'w-4 bg-white/10'}`}
              />
            ))}
          </div>
        </div>

        {/* Body */}
        <div className="p-6" data-testid={`wizard-step-${s.id}`}>
          {s.body}
        </div>

        {/* Footer */}
        <div className="p-5 border-t border-white/8 flex items-center justify-between gap-2 bg-white/[0.02]">
          <button
            onClick={dismiss}
            className="text-[11px] text-white/55 hover:text-white uppercase tracking-wider font-bold px-3 py-2"
            data-testid="wizard-skip"
          >
            Skip walkthrough
          </button>
          <div className="flex items-center gap-2">
            {step > 0 && (
              <button
                onClick={() => setStep((s) => Math.max(0, s - 1))}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-white/70 hover:text-white text-[11px] font-bold uppercase tracking-wider"
                data-testid="wizard-back"
              >
                <ArrowLeft size={12} /> Back
              </button>
            )}
            {!isLast && (
              <button
                onClick={() => setStep((s) => Math.min(STEPS.length - 1, s + 1))}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9] text-white text-[11px] font-bold uppercase tracking-wider hover:shadow-[0_8px_20px_rgba(139,92,246,0.4)]"
                data-testid="wizard-next"
              >
                Next <ArrowRight size={12} />
              </button>
            )}
            {isLast && (
              <button
                onClick={finish}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9] text-white text-[11px] font-bold uppercase tracking-wider hover:shadow-[0_8px_20px_rgba(139,92,246,0.4)]"
                data-testid="wizard-connect"
              >
                Connect my domain <ArrowRight size={12} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Convenience helper — read from localStorage whether the vendor has already
 * seen this wizard. Used by DashboardDomains to auto-open the modal on first
 * visit when they have no domains connected yet.
 */
export function hasDomainWizardBeenSeen() {
  try {
    return localStorage.getItem('wb:domain-wizard-seen') === '1';
  } catch {
    return false;
  }
}
